<template>
  <div class="page">
    <AppTopHeader />

    <main class="container login-main">
      <section class="login-card">
        <h2>{{ $t('auth.login.title') }}</h2>
        <p class="subtitle">{{ $t('auth.login.subtitle') }}</p>

        <form @submit.prevent="handleLogin">
          <div class="input-group">
            <label for="username">{{ $t('auth.login.usernameLabel') }}</label>
            <input type="text" id="username" v-model="username" required>
          </div>

          <div class="input-group">
            <label for="password">{{ $t('auth.login.passwordLabel') }}</label>
            <input :type="showPassword ? 'text' : 'password'" id="password" v-model="password" required>
            <span class="toggle-icon" @click="togglePassword">
              <i :class="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
            </span>
          </div>

          <router-link class="forgot-password" to="/reset-password">{{ $t('auth.login.forgotPassword') }}</router-link>

          <button type="submit" class="primary-button">{{ $t('auth.login.submitBtn') }}</button>
        </form>

        <button @click="goToRegister" class="register-button">{{ $t('auth.login.registerBtn') }}</button>
        <button @click="handleGoogleLogin" class="google-button">{{ $t('auth.login.googleBtn') }}</button>
      </section>
    </main>
  </div>
</template>

<script>
import AppTopHeader from "./AppTopHeader.vue";
import { flipmemoApiPath } from "@/config/flipmemoApi";
import { readResponseJson, attachHttpFallbackError } from "@/utils/readResponseJson";

/** Evita redirect=/my-account%20https://... no OAuth (localStorage ou query corruptos). */
function sanitizeReturnPath(raw) {
  if (raw == null || typeof raw !== "string") return "/my-account";
  const first = raw.trim().split(/\s+/)[0];
  if (!first.startsWith("/")) return "/my-account";
  return first;
}

export default {
  name: 'UserLogin',
  components: { AppTopHeader },
  data() {
    return {
      username: '',
      password: '',
      showPassword: false,
    };
  },
  methods: {
    handleGoogleLogin() {
      const target = sanitizeReturnPath(
        localStorage.getItem('preLoginRoute') || '/my-account'
      );
      const authURL = `${flipmemoApiPath("/auth/google")}?redirect=${encodeURIComponent(target)}`;
      window.location.href = authURL;
    },
    togglePassword() {
      this.showPassword = !this.showPassword;
    },
    async handleLogin() {
      const cleanedUsername = this.username.trim().toLowerCase();
      const cleanedPassword = this.password;

      try {
        const response = await fetch(flipmemoApiPath("/login"), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: cleanedUsername,
            password: cleanedPassword,
          }),
        });

        const raw = await readResponseJson(response);
        const data = attachHttpFallbackError(raw, response);

        if (response.status === 200) {
          localStorage.setItem('token', data.token);
          if (data.userId) {
            localStorage.setItem('userId', data.userId);
          } else if (data.id) {
            localStorage.setItem('userId', data.id);
          } else if (data.user && data.user.id) {
            localStorage.setItem('userId', data.user.id);
          }
          localStorage.setItem('username', cleanedUsername);
          this.$store.commit('setUsername', cleanedUsername);

          const preLoginRoute = sanitizeReturnPath(
            localStorage.getItem('preLoginRoute') || '/my-account'
          );
          localStorage.removeItem('preLoginRoute');
          this.$router.push(preLoginRoute);
        } else {
          alert(data.error);
        }
      } catch (error) {
        console.error('Erro ao fazer login:', error);
        alert(this.$t('auth.login.errorNetwork'));
      }
    },
    goToRegister() {
      this.$router.push('/register');
    },
  },
};
</script>

<style scoped>
.page { background: #0d1b2e; color: #e2eaf4; min-height: 100vh; font-family: Inter, "Segoe UI", sans-serif; }
.container { max-width: 1180px; margin: 0 auto; padding: 0 20px; }

.login-main { padding-top: 108px; padding-bottom: 40px; }
.login-card { max-width: 560px; margin: 0 auto; background: #152338; border: 1px solid #1e3352; border-radius: 14px; padding: 30px; box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3); }
h2 { margin: 0; font-size: 34px; letter-spacing: -0.02em; }
.subtitle { color: #94adc8; margin: 8px 0 18px; }

.input-group { margin-bottom: 14px; position: relative; }
label { display: block; margin-bottom: 8px; color: #94adc8; font-weight: 600; font-size: 14px; }
input { width: 100%; padding: 11px 40px 11px 12px; border: 1px solid #1e3352; border-radius: 10px; font-size: 15px; background: #081526; color: #e2eaf4; }
input:focus { outline: none; border-color: #4f8ef7; box-shadow: 0 0 0 3px rgba(79, 142, 247, 0.15); }
.toggle-icon { position: absolute; right: 12px; top: 39px; cursor: pointer; color: #4a6080; }

.forgot-password { display: inline-block; margin: 6px 0 14px; color: #4f8ef7; font-weight: 600; text-decoration: none; }
.primary-button { width: 100%; border: none; background: #4f8ef7; color: #fff; font-weight: 700; border-radius: 10px; padding: 12px; cursor: pointer; font-size: 16px; }
.primary-button:hover { background: #3b7ef0; }
.register-button { width: 100%; margin-top: 12px; border: none; background: #1a2e48; color: #94adc8; border-radius: 10px; padding: 12px; font-size: 16px; cursor: pointer; }
.register-button:hover { background: #1e3352; }
.google-button { width: 100%; margin-top: 10px; border: 1px solid #1e3352; background: #152338; color: #e2eaf4; border-radius: 10px; padding: 12px; font-size: 16px; cursor: pointer; font-weight: 700; }
.google-button:hover { background: #1a2e48; }

@media (max-width: 740px) {
  .login-card { padding: 22px; }
  h2 { font-size: 28px; }
}
</style>
